import { useCallback, useEffect, useState } from "react";

import type { ApiClient } from "../api/client";
import type { Dashboard } from "../api/types";
import { MonthNav } from "../components/MonthNav";
import { toMonthString, type MonthRef } from "../lib/dates";
import { formatDayLabel } from "../lib/dates";

interface Props {
  api: ApiClient;
  month: MonthRef;
  onMonthChange: (month: MonthRef) => void;
  onStepMonth: (delta: number) => void;
  targetUserId: string | null;
}

export function DashboardPage({
  api,
  month,
  onMonthChange,
  onStepMonth,
  targetUserId,
}: Props): JSX.Element {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  const monthString = toMonthString(month);
  const scope = targetUserId ?? undefined;

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.dashboard(monthString, scope));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load the dashboard.");
    }
  }, [api, monthString, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="page">
      <MonthNav month={month} onStep={onStepMonth} onChange={onMonthChange}>
        {data && <span className="month-total">{data.display_name}</span>}
      </MonthNav>

      {error && <p className="error">{error}</p>}
      {!data && !error && <p className="muted">Loading…</p>}

      {data && (
        <>
          <div className="stat-row">
            <Stat label="Total hours" value={data.duration_label} />
            <Stat label="Days logged" value={String(data.days_logged)} />
            <Stat label="Average per day" value={data.average_duration_label} />
            <Stat label="Entries" value={String(data.entry_count)} />
          </div>

          <div className="dashboard-columns">
            <section className="card">
              <h2>Time by task</h2>
              {data.by_task.length === 0 && <p className="muted">Nothing logged this month.</p>}
              <ul className="task-breakdown">
                {data.by_task.map((task) => (
                  <li key={task.task}>
                    <div className="task-line">
                      <span className="task-name">{task.task}</span>
                      <span className="task-duration">{task.duration_label}</span>
                    </div>
                    <div className="bar" aria-hidden="true">
                      <div className="bar-fill" style={{ width: `${task.percent}%` }} />
                    </div>
                    <span className="muted small">{task.percent}%</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="card">
              <h2>Recent entries</h2>
              {data.recent_entries.length === 0 && <p className="muted">Nothing yet.</p>}
              <ul className="recent-list">
                {data.recent_entries.map((entry) => (
                  <li key={entry.id}>
                    <div className="entry-main">
                      <span className="entry-task">{entry.task}</span>
                      <span className="entry-duration">{entry.duration_label}</span>
                    </div>
                    <span className="muted small">{formatDayLabel(entry.work_date)}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="stat card">
      <span className="stat-value">{value}</span>
      <span className="stat-label muted small">{label}</span>
    </div>
  );
}
